import React, { useEffect, useMemo, useState } from 'react';
import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { AlertColor, Box } from '@mui/material';
import ShortUniqueId from 'short-unique-id';
import {
  addCalculationField,
  deleteField,
  setSource,
  fieldDataType,
  addResultantDatatypeInCalculationFlow,
} from '../../../redux/Calculations/CalculationsActions';
import { minMax } from '../constants';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { isFlowFilledProperly, isFlowAlreadyPresentInStep } from '../utils';
import { isFlowUsed } from '../utils';

interface IDataTypeConversionDropZone {
  children: React.ReactNode;
  propKey: string;
}

export let sourceTypeField = ' ';
const DataTypeConversionDropZone = ({ children, propKey }: IDataTypeConversionDropZone) => {
  const uid = new ShortUniqueId({ length: 4 });
  const [showBorder, setShowBorder] = useState(false);
  const dispatch = useDispatch();
  const _calculations = useSelector((state: RootState) => state.calculations);

  const { calculationInfo, activeFlow, calculationUid } = useSelector((state: RootState) => {
    const currCalc = state.calculations.properties[propKey]?.currentCalculationSession;
    return {
      calculationInfo: currCalc?.calculationInfo || null,
      activeFlow: currCalc?.activeFlow || null,
      calculationUid: currCalc?.uuid || null,
    };
  });

  const disableUpdate = useMemo(() => {
    if (!calculationInfo || !activeFlow) return true;
    return isFlowUsed(
      Object.keys(calculationInfo.flows),
      calculationInfo.flows,
      activeFlow,
      calculationInfo,
    );
  }, [calculationInfo, activeFlow]);

  const isFieldPresent = useMemo(() => {
    if (!calculationInfo || !activeFlow) return false;

    const source = calculationInfo.flows?.[activeFlow]?.[0]?.source;
    return !!source?.[0];
  }, [calculationInfo, activeFlow]);

  const [alert, setAlert] = useState<{
    severity: AlertColor;
    message: string;
  } | null>(null);

  const [{ isOver }, drop] = useDrop({
    accept: ['card', 'calculationSelected'],
    hover: (item: any, monitor) => {
      if (monitor.isOver({ shallow: true }) && !showBorder) {
        setShowBorder(true);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
    drop: (item: any, monitor) => {
      if (disableUpdate) {
        setAlert({
          severity: 'warning',
          message:
            'This calculation is being used in another flow. Please remove the dependency before deleting.',
        });
        return;
      }
      if (!activeFlow || !calculationInfo) return;

      const prev = calculationInfo.flows[activeFlow][0].source;
      const prevSrcType = calculationInfo.flows[activeFlow][0].sourceType;

      if (calculationUid) {
        const newDataType = item.fieldData.dataType;
        if (prev[1] === newDataType) {
          setAlert({
            severity: 'error',
            message: `Cannot perform this  as type of ${item.fieldData.fieldname} is same as new datatype ${prev}.`,
          });
          return;
        }
      }
      if (item.type === 'calculationSelected') {
        if (!isFlowFilledProperly(calculationInfo, item.uid)) {
          setAlert({
            severity: 'warning',
            message: `Please ensure at least one field or flow is present and all conditions are met for flow ${item.name} before dropping.`,
          });
          return;
        }
        if (isFlowAlreadyPresentInStep(calculationInfo, item.uid, activeFlow, 0, 'source')) {
          const droppedFlowName =
            _calculations.properties[propKey]?.currentCalculationSession?.flowDisplayNames[
              item.uid
            ];
          const parentFlowName =
            _calculations.properties[propKey]?.currentCalculationSession?.flowDisplayNames[
              activeFlow
            ];
          setAlert({
            severity: 'warning',
            message: `${droppedFlowName} is already present inside ${parentFlowName}.`,
          });
          return;
        }
      }

      if (item.type === 'card') {
        const fieldId = uid();

        dispatch(
          addCalculationField(propKey, fieldId, {
            tableId: item.fieldData.tableId,
            fieldName: item.fieldData.fieldname,
            displayName: item.fieldData.displayname,
            dataType: item.fieldData.dataType,
          }),
        );

        sourceTypeField = 'field';
        dispatch(setSource(propKey, activeFlow, 0, fieldId, 'field', 0));
      } else if (item.type === 'calculationSelected') {
        if (item.fieldData.isAggregated) {
          setAlert({
            severity: 'error',
            message: 'Cannot add an aggregated field to an aggregation drop zone.',
          });
          return;
        }
        sourceTypeField = 'flow';
        dispatch(setSource(propKey, activeFlow, 0, item.uid, sourceTypeField, 0));
      }

      const datatype = item.fieldData.dataType;
      let value: string | null = prev[1];

      if (datatype === 'boolean') {
        if (['date', 'decimal', 'boolean'].includes(prev[1])) {
          value = null;
        }
      } else if (datatype === 'integer') {
        if (prev[1] === 'integer') {
          value = null;
        }
      } else if (datatype === 'decimal') {
        if (['decimal', 'boolean', 'date'].includes(prev[1])) {
          value = null;
        }
      } else if (datatype === 'text') {
        if (prev[1] === 'text') {
          value = null;
        }
      } else if (datatype === 'date') {
        if (['integer', 'decimal', 'boolean', 'date'].includes(prev[1])) {
          value = null;
        }
      } else if (datatype === 'timestamp') {
        if (['integer', 'decimal', 'boolean'].includes(prev[1])) {
          value = null;
        }
      } else value = prev[1];

      dispatch(setSource(propKey, activeFlow, 0, value ?? null, 'text', 1));
      dispatch(setSource(propKey, activeFlow, 0, prev[2] ?? false, 'boolean', 2));

      if (value === null) {
        dispatch(
          addResultantDatatypeInCalculationFlow(
            { uid: activeFlow, flow: activeFlow },
            propKey,
            '',
            0,
          ),
        );
      }

      if (prevSrcType[0] === 'field') {
        // existingFieldIds.forEach((existingFieldId) => {
        dispatch(deleteField(propKey, prev[0]));
        // });
      }
      setShowBorder(false);
    },
  });
  useEffect(() => {
    if (!isOver) {
      setShowBorder(false);
    }
  }, [isOver]);

  const dynamicDrag = isFieldPresent ? '35%' : '100%';
  return (
    <>
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={!!alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      <Box
        ref={drop}
        className={`dropzone ${showBorder ? 'dropzone-highlighted' : ''}`}
        sx={{
          cursor: 'pointer',
          textAlign: 'left',
          paddingBottom: '2rem',
          paddingTop: '5px',
          borderBottom: !showBorder ? '1px solid  #999999' : 'none',
          height: dynamicDrag,
          marginBottom: '0.5rem',
          border: '1px solid transparent',
        }}
      >
        {children}
      </Box>
    </>
  );
};

export default DataTypeConversionDropZone;
